import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { API, agentOutdated, timeAgo, fmtDate, fmtIdle } from "@/lib/api";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between border-b border-slate-200 pb-2 mb-3 text-[20px]">
        {title} {open ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
      </button>
      {open && children}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[180px_1fr] gap-3 py-1.5 text-[14px]">
    <span className="font-semibold text-slate-700">{label} :</span>
    <span className="text-slate-900 break-words">{value || ""}</span>
  </div>
);

const uptime = (s) => (s ? fmtIdle(s) : "");

export const InfoTab = ({ latest, m, group, noThumb }) => {
  const [tick, setTick] = useState(Date.now());
  const [thumbOk, setThumbOk] = useState(true);
  useEffect(() => {
    setThumbOk(true);
    const t = setInterval(() => setTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [m.machine_id]);

  return (
    <div data-testid="machine-info-tab">
      <p className="text-[14px] text-slate-700 text-center mb-4">Dernière mise à jour {timeAgo(m.last_seen)}</p>
      {!noThumb && <Section title="Capture d'écran">
        {thumbOk ? (
          <img data-testid="machine-thumbnail" src={`${API}/machines/${m.machine_id}/thumbnail?t=${tick}`} alt="Capture d'écran" onError={() => setThumbOk(false)} onLoad={() => setThumbOk(true)} className="mx-auto max-h-48 border border-slate-300 shadow-sm" />
        ) : (
          <p className="text-sm text-slate-500 text-center py-6" data-testid="no-thumbnail">Aucune capture disponible{m.online ? " pour l'instant" : " (machine hors ligne)"}.</p>
        )}
      </Section>}
      <Section title="Session">
        <Row label="Nom" value={m.display_name || m.hostname} />
        <Row label="Entreprise" value={group?.name} />
        <Row label="Site" value={m.site} />
        <Row label="Département" value={m.department} />
        <Row label="Type d'appareil" value={m.device_type || m.os} />
        <Row label="Hôtes connectés" value={m.technicians?.join(", ")} />
        <Row label="Invités connectés" value={m.online ? `Invité (${fmtDate(m.connected_at)})` : ""} />
        <Row label="Utilisateur connecté" value={m.username ? `${m.hostname}\\${m.username}` : ""} />
        <Row label="Temps d'inactivité" value={m.online ? fmtIdle(m.idle_s) : ""} />
      </Section>
      <Section title="Appareil">
        <Row label="Machine" value={m.hostname} />
        <Row label="Système d'exploitation" value={m.os_version} />
        <Row label="Processeur(s)" value={m.cpu} />
        <Row label="Mémoire" value={m.ram_gb ? `${m.ram_gb} Go` : ""} />
        <Row label="Écran" value={m.screen_w ? `${m.screen_w} × ${m.screen_h}` : ""} />
        <Row label="IP locale" value={m.ip_local} />
        <Row label="IP publique" value={m.ip_public} />
        <Row label="Allumé depuis" value={uptime(m.uptime_s)} />
        <Row label="Version de l'agent" value={m.agent_version ? (
          <span data-testid="info-agent-version">
            <span className={agentOutdated(m.agent_version, latest) ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{m.agent_version}</span>
            {latest && <span className="text-slate-500"> / {latest}{agentOutdated(m.agent_version, latest) ? " (à mettre à jour)" : " (à jour)"}</span>}
          </span>
        ) : "—"} />
        <Row label="Inscrit le" value={fmtDate(m.created_at)} />
      </Section>
    </div>
  );
};
